import React, { useEffect, useRef } from "react";
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { SRV_BASE_URL } from '../util/constants';
import MyDataLogo from "../img/mydata.png";
import useStore from './useStore';


const CHANNEL_NAME = "sse-toast-channel";

export const useSSEMessageHandler = () => {
    const { t } = useTranslation();

    const eventSourceRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const channelRef = useRef(null);

    const handleToast = (data) => {
        if (!data) return;

        const { errorMessage, messageType, message } = data;
        const toastId = `${messageType}-${message}`;

        // Prevent duplicates across tabs
        if (toast.isActive(toastId)) return;

        if (!errorMessage) {
            switch (messageType) {
                case "SEND_INVOICE_COMPLETED":
                    toast.success(
                        <p>
                            <img style={{ width: 80 }} src={MyDataLogo} alt="" />
                            {t("invoiceSended", { mark: message })}
                        </p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;

                case "RESEND_INVOICE_COMPLETED":
                    toast.success(
                        <p>
                            <img style={{ width: 80 }} src={MyDataLogo} alt="" />
                            {t("invoiceReSended", { mark: message })}
                        </p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;

                case "SUBMIT_PAYMENT_COMPLETED":
                    toast.success(
                        <p>{t("paymentSubmitted")}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;
                 case "SUBMIT_PAYMENT_FAILED":
                    toast.error(
                        <p>{t("paymentSubmitted")}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;

                case "SEND_INVOICE_COMPLETED_NO_MARK":
                    toast.success(
                        <p>
                            <img style={{ width: 80 }} src={MyDataLogo} alt="" />
                            {t("invoiceSendedNoMark", { signature: message })}
                        </p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;

                case "PAYMENT_CREATED":
                    toast.success(
                        <p>{t("paymentCreated", { signature: message })}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;

                case "TRANSACTION_COMPLETED":
                    toast.success(
                        <p>{t("transactionSucceded", { transactionId: message })}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;

                case "SEND_TRANSACTION_TO_POS":
                    toast.success(
                        <p>{t("transactionSentedToPos", { intentId: message })}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;
                case "TRANSACTION_NOT_COMPLETED":
                    toast.warn(
                        <p>{t("transactionNotSucceded")}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;
                case "REFRESH_TRANSACTION_NOT_COMPLETED":
                    toast.warn(
                        <p>{t("refreshTransactionNotSucceded", { transactionStatus: message })}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;
                case "TRANSACTION_CREATE_ERROR":
                    toast.error(
                        <p>{t("createTransactionError")}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;

                case "PAYMENT_CANCELED":
                    toast.success(
                        <p>{t("paymentCanceled")}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;
                case "POLLING_TIMED_OUT":
                    toast.error(
                        <p>{t("pollingTimedOut")}</p>,
                        { toastId, autoClose: 20000, position: "top-left" }
                    );
                    break;

                default:
                    break;
            }
        } else {
            toast.error(errorMessage, {
                toastId,
                autoClose: 20000,
                position: "bottom-left",
            });
        }
    };

    useEffect(() => {
        const company = useStore.getState().selectedCompany;
        if (!company?.id) return;

        // 🔄 Broadcast channel (cross-tab)
        const channel = new BroadcastChannel(CHANNEL_NAME);
        channelRef.current = channel;

        channel.onmessage = (event) => {
            handleToast(event.data);
        };

        const connect = () => {
            const es = new EventSource(`${SRV_BASE_URL}/sse/stream/${company.id}`, { withCredentials: true });
            eventSourceRef.current = es;

            es.onopen = () => {
                console.log("[SSE] Connection opened");
            };

            es.onerror = (err) => {
                console.error("[SSE] Error", err);
                es.close();

                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = setTimeout(connect, 5000);
            };

            es.onmessage = (e) => {
                try {
                    const data = JSON.parse(e.data);
                    console.log("[SSE] Received:", data);

                    // 🔊 Broadcast to all tabs
                    channel.postMessage(data);

                    // 🔔 Also show toast in this tab
                    handleToast(data);
                } catch (err) {
                    console.error("[SSE] Invalid JSON", err);
                }
            };
        };

        connect();

        return () => {
            console.log("[SSE] Cleanup");

            clearTimeout(reconnectTimerRef.current);

            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }

            if (channelRef.current) {
                channelRef.current.close();
            }
        };
    }, [t]);
};


